import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  Avatar,
  Box,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'

const totalsText = (totals) =>
  totals.length
    ? totals.map((entry) => `${entry.total} ${entry.unit}`).join(', ')
    : '—'

// One row per member of staff who has performed an imaging study - links
// through to the Radiation Doses tab on their employee page
const RadiationDoseOverview = ({ operators }) => {
  const navigate = useNavigate()

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Staff Member',
        minWidth: 200,
        flex: 1,
        valueGetter: (value, row) =>
          `${row.user?.firstName ?? ''} ${row.user?.lastName ?? ''}`.trim(),
        renderCell: ({ row }) => (
          <Stack direction="row" gap={1} alignItems="center" height="100%">
            <Avatar
              sx={{ width: 28, height: 28, borderRadius: 1 }}
              src={row.user?.photograph}
            />
            <Typography variant="body2">
              {row.user?.firstName} {row.user?.lastName}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'studiesPerformed',
        headerName: 'Studies',
        minWidth: 90,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => row.counts?.studiesPerformed ?? 0,
      },
      {
        field: 'ionisingStudies',
        headerName: 'Ionising',
        minWidth: 90,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => row.counts?.ionisingStudies ?? 0,
      },
      {
        field: 'dosesRecorded',
        headerName: 'Doses Recorded',
        minWidth: 130,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) => row.counts?.dosesRecorded ?? 0,
      },
      {
        field: 'last12Months',
        headerName: 'Dose (12 Months)',
        minWidth: 180,
        flex: 1,
        valueGetter: (value, row) => totalsText(row.totals?.last12Months ?? []),
      },
      {
        field: 'allTime',
        headerName: 'Dose (All Time)',
        minWidth: 180,
        flex: 1,
        valueGetter: (value, row) => totalsText(row.totals?.allTime ?? []),
      },
      {
        field: 'lastPerformedAt',
        headerName: 'Last Study',
        minWidth: 120,
        valueGetter: (value, row) => moment(row.lastPerformedAt).unix(),
        renderCell: ({ row }) => moment(row.lastPerformedAt).fromNow(),
      },
    ],
    [],
  )

  if (!operators?.length) return null

  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Stack gap={1}>
        <Stack direction="row" gap={1} alignItems="center">
          <MonitorHeartIcon color="warning" />
          <Typography variant="title3">Radiation Doses</Typography>
        </Stack>
        <Typography variant="caption">
          Imaging studies performed by staff, with radiation dose totals per
          unit. Click a row for that employee's full dose history.
        </Typography>
        <CustomDataGrid
          rows={operators}
          columns={columns}
          getRowId={(row) => row.user._id}
          maxHeight={420}
          hideFilters
          disableRowSelectionOnClick
          onRowClick={({ row }) =>
            navigate(`/employees/${row.user._id}?tab=radiationdoses`)
          }
          sx={{
            '.MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer',
            },
          }}
        />
      </Stack>
    </Paper>
  )
}

export default RadiationDoseOverview
